/**
 * MembershipDLQPage — /admin/memberships/dlq
 *
 * Lists dead-letter-queue entries for failed membership renewals.
 * Admins can resolve each entry via a dialog.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { PageContainer } from '@/components/layout';
import { Badge, Button, Card, Dropdown, Input, Modal } from '@/components/common';
import { useDLQEntries, useResolveDLQEntry } from '@features/memberships/api/hooks';
import type { MembershipDLQEntry, DLQResolution } from '@features/memberships/api/client';

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:last-child td {
    border-bottom: none;
  }
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: ${({ theme }) => theme.spacing.md};
`;

const FiltersRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  cursor: pointer;
`;

const ErrorMsg = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.error};
  margin: 2px 0 0;
`;

// ---------------------------------------------------------------------------
// Resolve dialog
// ---------------------------------------------------------------------------

interface ResolveDialogProps {
  entry: MembershipDLQEntry | null;
  onClose: () => void;
}

const RESOLUTION_OPTIONS: { value: DLQResolution; label: string; testId: string }[] = [
  { value: 'retry',          label: 'Retry now',                          testId: 'candeza-memberships-dlq-resolve-retry-option' },
  { value: 'skip',           label: 'Skip this renewal',                  testId: 'candeza-memberships-dlq-resolve-skip-option' },
  { value: 'pause',          label: 'Pause the membership',               testId: 'candeza-memberships-dlq-resolve-pause-option' },
  { value: 'mark_resolved',  label: 'Mark as resolved without retry',     testId: 'candeza-memberships-dlq-resolve-mark-option' },
];

const ResolveDialog: React.FC<ResolveDialogProps> = ({ entry, onClose }) => {
  const resolveMut = useResolveDLQEntry();
  const [resolution, setResolution] = useState<DLQResolution>('retry');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState('');

  const handleSubmit = async () => {
    if (notes.trim().length < 10) {
      setErr('Notes must be at least 10 characters');
      return;
    }
    setErr('');
    if (!entry) return;
    await resolveMut.mutateAsync({ dlqId: entry.id, body: { resolution, notes } });
    onClose();
  };

  return (
    <Modal
      isOpen={Boolean(entry)}
      onClose={onClose}
      title="Resolve DLQ entry"
      size="md"
      data-testid="candeza-memberships-dlq-resolve-dialog"
    >
      {entry && (
        <>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
            Membership: {entry.membership_id} · Failure: {entry.failure_code}
          </p>
          <p style={{ fontSize: 13, marginBottom: 0 }}>{entry.failure_message}</p>

          <RadioGroup>
            {RESOLUTION_OPTIONS.map((opt) => (
              <RadioLabel key={opt.value}>
                <input
                  type="radio"
                  name="resolution"
                  value={opt.value}
                  checked={resolution === opt.value}
                  onChange={() => setResolution(opt.value)}
                  data-testid={opt.testId}
                />
                {opt.label}
              </RadioLabel>
            ))}
          </RadioGroup>

          <div>
            <Input
              label="Resolution notes (min 10 chars) *"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe the resolution action taken…"
            />
            {err && <ErrorMsg>{err}</ErrorMsg>}
          </div>
        </>
      )}
      <ModalActions>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          data-testid="candeza-memberships-dlq-resolve-confirm-btn"
          isLoading={resolveMut.isPending}
          onClick={() => void handleSubmit()}
        >
          Resolve
        </Button>
      </ModalActions>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const MembershipDLQPage: React.FC = () => {
  const [resolvedFilter, setResolvedFilter] = useState<string>('');
  const [resolving, setResolving] = useState<MembershipDLQEntry | null>(null);
  const [failureCodeFilter, setFailureCodeFilter] = useState('');

  const resolved = resolvedFilter === 'true' ? true : resolvedFilter === 'false' ? false : undefined;
  const { data, isLoading, isError, refetch } = useDLQEntries(resolved);

  const allEntries = data?.items ?? [];

  // Client-side failure_code filter
  const entries = failureCodeFilter
    ? allEntries.filter((e) => e.failure_code === failureCodeFilter)
    : allEntries;

  const uniqueFailureCodes = [...new Set(allEntries.map((e) => e.failure_code))];

  return (
    <PageContainer
      title="Membership DLQ"
      subtitle="Failed renewal entries requiring admin resolution"
      data-testid="candeza-memberships-dlq-page"
    >
      <FiltersRow>
        <Dropdown
          label=""
          placeholder="All statuses"
          value={resolvedFilter}
          onChange={setResolvedFilter}
          options={[
            { value: '', label: 'All' },
            { value: 'false', label: 'Open' },
            { value: 'true', label: 'Resolved' },
          ]}
        />
        {uniqueFailureCodes.length > 0 && (
          <Dropdown
            label=""
            placeholder="All failure codes"
            value={failureCodeFilter}
            onChange={setFailureCodeFilter}
            options={[
              { value: '', label: 'All failure codes' },
              ...uniqueFailureCodes.map((c) => ({ value: c, label: c })),
            ]}
          />
        )}
      </FiltersRow>

      {isLoading && (
        <Center>Loading DLQ entries…</Center>
      )}

      {isError && !isLoading && (
        <Center>
          <span>Failed to load DLQ entries.</span>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </Center>
      )}

      {!isLoading && !isError && entries.length === 0 && (
        <Center>
          <span>No DLQ entries found.</span>
        </Center>
      )}

      {!isLoading && !isError && entries.length > 0 && (
        <Card padding="none">
          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <Th>Occurred</Th>
                  <Th>Membership</Th>
                  <Th>Failure Code</Th>
                  <Th>Message</Th>
                  <Th>Retries</Th>
                  <Th>Status</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry: MembershipDLQEntry) => (
                  <Tr
                    key={entry.id}
                    data-testid={`candeza-memberships-dlq-row-${entry.id}`}
                  >
                    <Td title={entry.occurred_at}>
                      {new Date(entry.occurred_at).toLocaleDateString()}
                    </Td>
                    <Td>
                      <Link to={`/admin/memberships/${entry.membership_id}`}>
                        {entry.membership_id}
                      </Link>
                    </Td>
                    <Td>
                      <code style={{ fontSize: 11 }}>{entry.failure_code}</code>
                    </Td>
                    <Td title={entry.failure_message}>
                      {entry.failure_message.slice(0, 80)}
                      {entry.failure_message.length > 80 ? '…' : ''}
                    </Td>
                    <Td>{entry.retry_count}</Td>
                    <Td>
                      <Badge variant={entry.resolved ? 'success' : 'error'}>
                        {entry.resolved ? 'Resolved' : 'Open'}
                      </Badge>
                    </Td>
                    <Td>
                      {!entry.resolved && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setResolving(entry)}
                        >
                          Resolve
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
        </Card>
      )}

      <ResolveDialog entry={resolving} onClose={() => setResolving(null)} />
    </PageContainer>
  );
};
